import type { Directive } from "vue";
import { listShortcutHelp } from "@/shared/composables/useShortcut";

/**
 * ⌘F (Ctrl+F off Apple) puts the cursor in the page's own search field, where it has one.
 *
 * A field opts in with `v-page-search`, which marks it `data-page-search`. The key handler
 * (`usePageSearchShortcut`, installed once in App.vue) looks for a marked field that can take
 * the focus, and lets the browser's find open instead whenever it should — see
 * `handlePageSearchKey`. The browser's find is never far: a second ⌘F, from the field itself,
 * opens it.
 */

export const PAGE_SEARCH_ATTRIBUTE = "data-page-search";

const PAGE_SEARCH_HELP = { key: "f", labelKey: "common.shortcuts.pageSearch" };

/**
 * ⌘F on Apple, Ctrl+F elsewhere — and nothing else. On a Mac, Ctrl+F is a text-editing key
 * (forward one character), and off one, the Windows key is not a modifier anyone presses for
 * this; Shift or Alt with F is some other binding.
 */
export function isPageSearchChord(event: KeyboardEvent, apple: boolean): boolean {
	if (event.key.toLowerCase() !== "f" || event.shiftKey || event.altKey) return false;
	return apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

/**
 * Whether `el` cannot be seen or reached: hidden by an attribute or by its style, or inside an
 * `inert` or `aria-hidden` subtree (a dialog's backdrop makes the page behind it both). Judged
 * from attributes and computed style, never from layout, which a test DOM does not have.
 */
function isConcealed(el: Element): boolean {
	const view = el.ownerDocument.defaultView;
	for (let node: Element | null = el; node; node = node.parentElement) {
		if (node instanceof HTMLElement && node.hidden) return true;
		if (node.hasAttribute("inert") || node.getAttribute("aria-hidden") === "true") return true;
		const style = view?.getComputedStyle(node);
		if (style && (style.display === "none" || style.visibility === "hidden")) return true;
	}
	return false;
}

type SearchField = HTMLInputElement | HTMLTextAreaElement;

/** The input a marked element stands for: itself, or the one inside it (a wrapped HabInput). */
function fieldOf(marked: Element): SearchField | null {
	if (marked instanceof HTMLInputElement || marked instanceof HTMLTextAreaElement) return marked;
	return marked.querySelector<SearchField>("input, textarea");
}

/** Open dialogs in document order; the last is on top, since each portals in after the last. */
function openDialogs(doc: Document): Element[] {
	return [...doc.querySelectorAll('[role="dialog"], [role="alertdialog"], dialog[open]')].filter(
		(d) => !isConcealed(d),
	);
}

/**
 * The search ⌘F should focus: the first visible, enabled marked field — inside the topmost open
 * dialog when there is one, since everything behind a dialog is out of reach.
 */
export function findPageSearch(doc: Document): SearchField | null {
	const scope: ParentNode = openDialogs(doc).at(-1) ?? doc;
	for (const marked of scope.querySelectorAll(`[${PAGE_SEARCH_ATTRIBUTE}]`)) {
		const field = fieldOf(marked);
		if (field && !field.disabled && !isConcealed(field)) return field;
	}
	return null;
}

/**
 * Focuses and selects the page's search for ⌘F, and says whether it did. Leaves the event to
 * the browser — its own find — when:
 *
 * - another handler already took it (`defaultPrevented`);
 * - the focus is in a Monaco editor, which has a find of its own;
 * - there is no field to focus;
 * - the focus is already in that field, so a second ⌘F still reaches the browser's find.
 *
 * It acts from any other input, though: from a form field, ⌘F still means "search the page".
 */
export function handlePageSearchKey(
	event: KeyboardEvent,
	options: { apple: boolean; doc: Document },
): boolean {
	if (event.defaultPrevented || !isPageSearchChord(event, options.apple)) return false;
	const active = options.doc.activeElement;
	if (active?.closest(".monaco-editor")) return false;
	const field = findPageSearch(options.doc);
	if (!field || field === active) return false;
	event.preventDefault();
	field.focus();
	field.select();
	return true;
}

// ── v-page-search ──────────────────────────────────────────────────────────────

const delist = new WeakMap<HTMLElement, () => void>();

function mark(el: HTMLElement, on: boolean): void {
	if (on && !delist.has(el)) {
		el.setAttribute(PAGE_SEARCH_ATTRIBUTE, "");
		// The help popover lists ⌘F while a page search is mounted, from the same registry the
		// bound shortcuts use.
		delist.set(el, listShortcutHelp(PAGE_SEARCH_HELP.key, PAGE_SEARCH_HELP.labelKey));
	} else if (!on) {
		const off = delist.get(el);
		if (!off) return;
		el.removeAttribute(PAGE_SEARCH_ATTRIBUTE);
		off();
		delist.delete(el);
	}
}

/**
 * Marks a search field as the page's, for ⌘F. On a component, it marks the component's root,
 * and the handler finds the input inside. `v-page-search="false"` leaves a field unmarked.
 */
export const vPageSearch: Directive<HTMLElement, boolean | undefined> = {
	mounted: (el, binding) => mark(el, binding.value !== false),
	updated: (el, binding) => mark(el, binding.value !== false),
	beforeUnmount: (el) => mark(el, false),
};
